import { Component, OnInit } from "@angular/core";
import { Equity } from "src/app/_models/equity";
import { Chart } from "angular-highcharts";
import { GenerateLineChartService } from "src/app/_services/generate-line-chart.service";
import { CurrencyPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { tap } from 'rxjs/operators';
import { HttpRequestService } from 'src/app/_services/http-request.service';

@Component({
  selector: "app-equity",
  templateUrl: "./equity.component.html",
  styleUrls: ["./equity.component.css"]
})

export class EquityComponent implements OnInit {

  equityData: Equity[] = [];
  lineChart: Chart;
  url = environment.baseUrl + environment.baseHref;

  constructor(private httpRequestService: HttpRequestService, private lineChartGenerator: GenerateLineChartService, private translate: TranslateService) { }

  ngOnInit() {
    this.handleChartTranslation();
    this.getEquityAndPopulateChart();
  }

  getEquityAndPopulateChart() {
    return this.httpRequestService.equityData.pipe(tap(responseData => {
      this.resetValues();
      if (responseData.length === 0)
        return;
      this.equityData = responseData;
      this.createChart(this.equityData[0].currency);
    })).toPromise();
  }

  handleChartTranslation() {
    this.translate.stream('account-preview.equity-chart').subscribe(translationOf => {
      if (this.lineChart) {
        this.lineChart.ref$.subscribe(chart => {
          chart.series[0].update({ name: translationOf['portfolio-value'], type: undefined }, true);
          chart.series[1].update({
            name: translationOf['deposits'], tooltip: {
              pointFormatter: this.translateTooltip()
            }, type: undefined
          }, true);
          chart.series[2].update({
            name: translationOf['withdrawals'], tooltip: {
              pointFormatter: this.translateTooltip()
            }, type: undefined
          }, true);
        });
      }
    });
  }

  createChart(baseCurrency: string) {
    this.lineChart = this.lineChartGenerator.getChart();
    this.populateChart();

    this.lineChart.ref$.subscribe(responseChart => {
      responseChart.update({
        xAxis: {
          type: 'datetime'
        },
        yAxis: {
          labels: {
            formatter: function () {
              return (new CurrencyPipe('en-US')).transform(this.value, baseCurrency, 'symbol');
            }
          }
        }
      }, true, true);
    });
  }

  populateChart() {
    let portfolioValues = [];
    let deposits = [];
    let withdrawals = [];

    this.equityData.forEach((equityElement: any, dayIndex: number) => {
      // Create the the tuples [date, value]
      portfolioValues.push({ x: new Date(equityElement.date).valueOf(), y: equityElement.value, currency: equityElement.currency });

      if (equityElement.valueTransfers === 0 || Math.abs(equityElement.valueTransfers) < 1) return;

      let picturedTransferDate = null;
      let actualTransferDate = null;
      let valueExludingTransfers = null;

      if (dayIndex === 0) {
        picturedTransferDate = new Date(equityElement.date);
        picturedTransferDate.setDate(picturedTransferDate.getDate() - 1); // go back 1 day
        actualTransferDate = new Date(equityElement.date);
        valueExludingTransfers = (equityElement.value - equityElement.valueTransfers);
        portfolioValues.unshift({ x: picturedTransferDate.valueOf(), y: valueExludingTransfers, currency: equityElement.currency });
      } else {
        picturedTransferDate = new Date(this.equityData[dayIndex - 1].date);
        actualTransferDate = new Date(this.equityData[dayIndex].date);
        valueExludingTransfers = this.equityData[dayIndex - 1].value;
      }
      let { valueWithCurrency, transferImg } = this.createTooltipVariables(equityElement);

      equityElement.valueTransfers > 0 ? deposits.push({ x: picturedTransferDate.valueOf(), y: valueExludingTransfers, valueWithCurrency: valueWithCurrency, actualTransferDate: new Date(actualTransferDate).toLocaleDateString('en-GB', { timeZone: 'UTC' }), img: transferImg } as any) :
        withdrawals.push({ x: picturedTransferDate.valueOf(), y: valueExludingTransfers, valueWithCurrency: valueWithCurrency, actualTransferDate: new Date(actualTransferDate).toLocaleDateString('en-GB', { timeZone: 'UTC' }), img: transferImg } as any);
    });

    this.addPortfolioValuesToChart(portfolioValues);
    this.addDepositsToChart(deposits);
    this.addWithdrawalsToChart(withdrawals);
  }

  createTooltipVariables(equityElement: any) {
    let valueWithCurrency = (new CurrencyPipe('en-US')).transform(Math.abs(parseFloat(equityElement.valueTransfers.toFixed(2))), equityElement.currency, 'symbol');
    let transferImg = '<span><img src = "' + this.url + 'assets/images/' + (equityElement.valueTransfers > 0 ? 'plus' : 'minus') + '-circle-duotone-resized-10x10.svg" alt="plus-minus" height="7" width="7"/> ';
    return { valueWithCurrency, transferImg };
  }

  addPortfolioValuesToChart(portfolioValues: any[]) {
    this.lineChart.addSeries({
      name: this.translate.instant('account-preview.equity-chart.portfolio-value'),
      data: portfolioValues,
      type: undefined,
      tooltip: {
        pointFormatter: function () {
          let pointWithColor = '<span class="highcharts-color-' + this.colorIndex + '">\u25CF</span> ';
          let value = (new CurrencyPipe('en-US')).transform(this.y, this['currency'], 'symbol');
          return pointWithColor + this.series.name + ': <strong>' + value + '</strong><br>';
        }
      }
    }, true, false);
  }

  addDepositsToChart(deposits: any[]) {
    this.lineChart.addSeries({
      name: this.translate.instant('account-preview.equity-chart.deposits'),
      data: deposits,
      type: undefined,
      showInLegend: false,
      lineColor: 'none',
      fillColor: 'none',
      marker: { enabled: true, symbol: 'url(' + this.url + 'assets/images/plus-circle-duotone-resized-10x10.svg)' },
      tooltip: {
        pointFormatter: this.translateTooltip()
      }
    }, true, false);
  }

  addWithdrawalsToChart(withdrawals: any[]) {
    this.lineChart.addSeries({
      name: this.translate.instant('account-preview.equity-chart.withdrawals'),
      data: withdrawals,
      type: undefined,
      showInLegend: false,
      lineColor: 'none',
      fillColor: 'none',
      marker: { enabled: true, symbol: 'url(' + this.url + 'assets/images/minus-circle-duotone-resized-10x10.svg)' },
      tooltip: {
        pointFormatter: this.translateTooltip()
      }
    }, true, false);
  }

  translateTooltip() {
    const dateTranslated = this.translate.instant('account-preview.equity-chart.date');
    return function () {
      const content = this.series.name + ': <strong>' + this['valueWithCurrency'] + '</strong></span><br>';
      const note = '<small><em>' + this.series.name + ' ' + dateTranslated + ': ' + this['actualTransferDate'] + '</em></small>';
      return this['img'] + content + note;
    };
  }

  resetValues() {
    this.equityData = [];
  }
}
